import React, { useState } from "react";
import { campaignRepo, customerRepo, invoiceRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, DataTable, Input, Modal, NotConnected, PageHead, Select, StatusBadge, Textarea, formatDate, useAsync, useTableState } from "../../components/ui";

const SEGMENTS = ["ลูกค้าทั้งหมด", "สมาชิก Gold ขึ้นไป", "ลูกค้าที่ไม่มาเกิน 90 วัน", "ลูกหนี้ค้างชำระ", "ลูกค้าใหม่เดือนนี้"];
const CHANNELS = ["line", "sms", "email"];
const EMPTY = { name: "", channel: "line", segment: SEGMENTS[0], message: "" };

// นับลูกค้าจริงตามกลุ่มเป้าหมาย (ลูกค้าที่ยังใช้งานอยู่เท่านั้น)
const DAY = 86400000;
export const audienceOf = (segment, customers = [], invoices = []) => {
  const active = customers.filter((c) => c.isActive !== false);
  const now = Date.now();
  switch (segment) {
    case "สมาชิก Gold ขึ้นไป":
      return active.filter((c) => ["gold", "platinum", "diamond"].includes(String(c.tier).toLowerCase()));
    case "ลูกค้าที่ไม่มาเกิน 90 วัน": // = ไม่ได้มาใช้บริการเกิน 90 วัน (รวมที่ไม่เคยมา)
      return active.filter((c) => !c.lastVisitAt || now - new Date(c.lastVisitAt).getTime() > 90 * DAY);
    case "ลูกหนี้ค้างชำระ": {
      const debtors = new Set(invoices.filter((inv) => inv.status === "overdue" || (inv.status === "unpaid" && inv.dueAt && new Date(inv.dueAt) < now)).map((inv) => inv.customerId));
      return active.filter((c) => debtors.has(c.id));
    }
    case "ลูกค้าใหม่เดือนนี้": {
      const month = new Date().toISOString().slice(0, 7);
      return active.filter((c) => String(c.createdAt).slice(0, 7) === month);
    }
    default:
      return active;
  }
};

export default function Campaigns() {
  const { orgId, toast } = useApp();
  const { tx } = useI18n();
  const tableState = useTableState();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data, loading, reload } = useAsync(
    () => campaignRepo.list(orgId, { page: tableState.page, perPage: tableState.perPage, search: tableState.search, sortBy: "createdAt", sortDir: "desc" }),
    [orgId, tableState.page, tableState.perPage, tableState.search]
  );

  const { data: customers } = useAsync(() => customerRepo.all(orgId), [orgId], []);
  const { data: invoices } = useAsync(() => invoiceRepo.all(orgId), [orgId], []);
  const audience = (segment) => audienceOf(segment, customers || [], invoices || []).length;

  const save = async () => {
    if (!form.name) return toast(tx("กรอกชื่อแคมเปญ", "Campaign name required"), "error");
    await campaignRepo.create(orgId, { ...form, status: "draft", audienceCount: audience(form.segment), sentCount: 0 });
    setOpen(false);
    setForm(EMPTY);
    reload();
    toast(tx("สร้างแคมเปญเรียบร้อย", "Campaign created"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("แคมเปญ & Broadcast", "Campaigns & Broadcast")}
        subtitle={tx("เตรียมข้อความและกลุ่มเป้าหมายเก็บเป็นฉบับร่าง — ยังส่งออกจริงไม่ได้จนกว่าจะเชื่อมต่อช่องทาง", "Prepare messages and audiences as drafts — real sending needs a connected channel")}
        actions={
          <>
            <NotConnected label={tx("ช่องทางส่ง", "Sending channel")} hint="LINE / SMS / Email" />
            <Button variant="primary" onClick={() => setOpen(true)}>+ {tx("สร้างแคมเปญ", "New campaign")}</Button>
          </>
        }
      />

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีแคมเปญ", "No campaigns")}
        columns={[
          { key: "name", label: tx("ชื่อแคมเปญ", "Campaign") },
          { key: "channel", label: tx("ช่องทาง", "Channel"), render: (row) => <Badge tone="indigo">{row.channel}</Badge> },
          { key: "segment", label: tx("กลุ่มเป้าหมาย", "Segment") },
          { key: "audienceCount", label: tx("จำนวนคน", "Audience"), align: "right", render: (row) => (row.status === "sent" ? row.audienceCount : audience(row.segment)) },
          { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
          {
            key: "action",
            label: "",
            render: (row) => (row.status !== "sent" ? <NotConnected label={tx("ช่องทางส่ง", "Channel")} /> : formatDate(row.sentAt)),
          },
        ]}
      />

      <Modal
        open={open}
        title={tx("สร้างแคมเปญใหม่", "New campaign")}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="primary" onClick={save}>{tx("บันทึกร่าง", "Save draft")}</Button>
          </>
        }
      >
        <Input label={tx("ชื่อแคมเปญ", "Campaign name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Select label={tx("ช่องทาง", "Channel")} options={CHANNELS} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} />
        <Select
          label={tx("กลุ่มเป้าหมาย", "Segment")}
          hint={tx(`ลูกค้าที่ตรงกลุ่มนี้ตอนนี้ ${audience(form.segment)} คน`, `${audience(form.segment)} matching customers now`)}
          options={SEGMENTS}
          value={form.segment}
          onChange={(e) => setForm({ ...form, segment: e.target.value })}
        />
        <Textarea label={tx("ข้อความ", "Message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="สวัสดีค่ะ คุณ {{ชื่อ}} ..." />
      </Modal>
    </>
  );
}
